import { ru } from "date-fns/locale";
import { CalendarDays, ChevronDown, Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { at, dateOf, dayOf, dayOfDate, hourOf } from "../lib/time";
import { cn } from "../lib/utils";

/** Шаг времени — как в планировщике YouTube, которым пользуется Артур (09.10.2026). */
const STEP = 15;
const SLOTS = Array.from({ length: (24 * 60) / STEP }, (_, i) => i * STEP);

const PILL = "inline-flex h-8 items-center gap-1.5 rounded-full border bg-background pl-3 pr-2.5 text-[13px] font-medium hover:bg-muted";

const toDate = (day: number) => {
  const [y, m, d] = dateOf(day).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const dateLabel = (day: number) => toDate(day).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" });
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/** День из календаря. `day` пусто — кнопка с подписью `placeholder` («Всем сразу»). */
export function DatePick({ day, now, onPick, placeholder }: { day: number | null; now: number; onPick: (day: number) => void; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const today = dayOf(now);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={PILL}>
          <CalendarDays className="size-3.5 text-muted-foreground" />
          {day === null ? placeholder : dateLabel(day)}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={ru}
          weekStartsOn={1}
          selected={day === null ? undefined : toDate(day)}
          defaultMonth={toDate(day ?? today)}
          disabled={{ before: toDate(today) }}
          onSelect={(d) => {
            if (!d) return;
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            onPick(dayOfDate(iso));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Время списком с шагом 15 минут. `minutes` пусто — подпись `placeholder`.
 * `day` задан — прошедшее время этого дня недоступно.
 */
export function TimePick({ minutes, day, now, onPick, placeholder }: { minutes: number | null; day?: number; now: number; onPick: (minutes: number) => void; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Список открывается на выбранном, а не на 00:00 (без выбора — на 18:00).
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLElement>("[data-selected], [data-anchor]")?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={PILL}>
          <Clock className="size-3.5 text-muted-foreground" />
          {minutes === null ? placeholder : hhmm(minutes)}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-28 p-1" align="start">
        <div ref={listRef} className="max-h-64 overflow-y-auto">
          {SLOTS.map((m) => {
            const past = day !== undefined && at(day, 0, m) <= now;
            const selected = m === minutes;
            return (
              <button
                key={m}
                type="button"
                disabled={past}
                data-selected={selected || undefined}
                data-anchor={minutes === null && m === 18 * 60 ? true : undefined}
                onClick={() => { onPick(m); setOpen(false); }}
                className={cn(
                  "block w-full rounded-md px-3 py-1.5 text-left text-sm tabular-nums",
                  selected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                  past && "cursor-not-allowed opacity-35 hover:bg-transparent",
                )}
              >
                {hhmm(m)}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Срок публикации: дата и время отдельно, оба — выпадающие (09.10.2026).
 *
 * Было «до 12…20:00» плюс «Другая дата и время…» за лишним кликом. Ролики
 * планируют сразу на несколько дней вперёд, и каждый раз раскрывать «другую
 * дату» — лишние действия. Теперь: дата из календаря, время из списка с шагом
 * 15 минут.
 *
 * `due` — минуты от понедельника (см. lib/time). Прошедшее время недоступно.
 */
export function DueDateTime({ due, now, onChange }: { due: number; now: number; onChange: (due: number) => void }) {
  const day = dayOf(due);
  const minutes = Math.round(hourOf(due) * 60);

  const setDay = (d: number) => {
    let next = at(d, 0, minutes);
    // Сегодня, а выбранное время уже прошло — ближайший свободный слот.
    if (next <= now) next = at(d, 0, Math.ceil(((now - at(d, 0)) + 1) / STEP) * STEP);
    onChange(next);
  };

  return (
    <>
      <DatePick day={day} now={now} onPick={setDay} />
      <TimePick minutes={minutes} day={day} now={now} onPick={(m) => onChange(at(day, 0, m))} />
    </>
  );
}
